import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import { QButton, QStack, QText } from '@captivateiq/quotient';

import { useAIShellContext } from '@captivateiq/assistant/AIShellContext';
import type { ApprovalDecision, PendingApproval } from '@captivateiq/assistant/types';

type PendingApprovalCardProps = {
  approval: PendingApproval;
};

/**
 * Approve/decline control for a write action the agent has queued but not executed.
 *
 * Both buttons submit to the backend — declining is a persisted denial that the agent sees,
 * not a local dismissal. Both carry `approval_id`, so the decision resolves exactly the action
 * shown here and nothing else.
 *
 * Title and details come from the backend, rendered from the tool's validated arguments. They
 * are shown as plain text: the agent does not author this copy, and tool arguments are not
 * trusted as markup.
 */
export const PendingApprovalCard = ({ approval }: PendingApprovalCardProps) => {
  const { t } = useTranslation('assistant');
  const { resolveApproval, agentThinking } = useAIShellContext();
  const submittingRef = useRef(false);

  const handleDecision = useCallback(
    async (decision: ApprovalDecision) => {
      if (agentThinking || submittingRef.current) return;
      submittingRef.current = true;
      try {
        await resolveApproval(approval.approval_id, decision);
      } finally {
        submittingRef.current = false;
      }
    },
    [resolveApproval, agentThinking, approval.approval_id],
  );

  return (
    <QStack
      direction="column"
      spacing="8px"
      sx={{
        backgroundColor: 'white',
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: '6px',
        mt: 0.5,
        padding: '10px 12px',
      }}
    >
      <QText sx={{ color: 'grey.700', fontSize: '12px', fontWeight: 600, lineHeight: 1.4 }}>
        {approval.action.title}
      </QText>

      {approval.action.details.length > 0 && (
        <QStack direction="column" spacing="2px">
          {approval.action.details.map((detail, index) => (
            <QStack
              direction="row"
              // oxlint-disable-next-line react/no-array-index-key
              key={`${detail.label}-${index}`}
              spacing="6px"
              sx={{ alignItems: 'baseline', minWidth: 0 }}
            >
              <QText sx={{ color: 'grey.500', flexShrink: 0, fontSize: '11px', fontWeight: 600, lineHeight: 1.4 }}>
                {detail.label}
              </QText>
              <QText sx={{ color: 'grey.700', fontSize: '11px', lineHeight: 1.4, wordBreak: 'break-word' }}>
                {detail.value}
              </QText>
            </QStack>
          ))}
        </QStack>
      )}

      <QStack direction="row" spacing="8px">
        <QButton
          color="primary"
          disabled={agentThinking}
          size="small"
          startIcon={faCheck}
          variant="contained"
          onClick={() => handleDecision('approved')}
        >
          {t('button_labels.approve')}
        </QButton>
        <QButton
          color="error"
          disabled={agentThinking}
          size="small"
          startIcon={faXmark}
          variant="outlined"
          onClick={() => handleDecision('denied')}
        >
          {t('button_labels.decline')}
        </QButton>
      </QStack>
    </QStack>
  );
};
